/**
 * Configuration for phone numbers that should trigger a warning before dialing.
 *
 * These are contacts we prefer staff reach by email rather than by phone. When
 * a user attempts to dial one of these numbers, a confirmation pop-up is shown
 * (gated by the `showRestrictedDialWarning` feature flag). The user can still
 * proceed with the call, but is nudged toward emailing instead.
 *
 * For now this is a hardcoded list. If this grows or needs to vary by
 * environment, consider moving it to config/data.
 * @module
 */

// A US phone number with the leading `1` country code is 11 digits.
const US_PHONE_LENGTH_WITH_COUNTRY_CODE = 11

/**
 * Strip a phone number down to digits only, dropping a leading US country code
 * so that numbers written in different formats compare equal. e.g.
 * `(901) 683-0055`, `901-683-0055`, and `+19016830055` all normalize to
 * `9016830055`.
 * @param phoneNumber raw phone number string
 * @returns digits-only national number, without a leading `1` country code
 */
export function normalizePhoneNumber(phoneNumber: string): string {
  const digits = phoneNumber.replaceAll(/\D/g, '')
  // Drop a leading US country code if present
  if (
    digits.length === US_PHONE_LENGTH_WITH_COUNTRY_CODE &&
    digits.startsWith('1')
  ) {
    return digits.slice(1)
  }
  return digits
}

/**
 * West Cancer Center phone numbers. West prefers email communications, so
 * dialing these surfaces a warning. Stored normalized for reliable matching.
 */
export const RESTRICTED_DIAL_NUMBERS: ReadonlySet<string> = new Set(
  [
    '901-683-0055',
    '901-692-9600',
    '901-922-6798',
    '901-609-3501',
    '662-286-3694',
    '662-349-9556',
    '731-285-2211',
    '870-207-8177',
    '901-685-5520',
    '901-300-1562',
    '731-644-3522',
    '714-625-6708', // TEST NUMBER (not a real West number, but included in the restricted list for testing)
  ].map(normalizePhoneNumber)
)

/**
 * Whether dialing the given number should trigger the warning pop-up.
 * @param phoneNumber raw phone number being dialed
 * @returns true if the number is on the restricted list
 */
export function isRestrictedDialNumber(phoneNumber: string): boolean {
  return RESTRICTED_DIAL_NUMBERS.has(normalizePhoneNumber(phoneNumber))
}

/**
 * Body copy shown in the warning pop-up.
 */
export const RESTRICTED_DIAL_WARNING_TEXT =
  'You are attempting to contact a West Cancer Center phone number. ' +
  'West prefers email communications instead.'

/**
 * Title shown in the warning pop-up.
 */
export const RESTRICTED_DIAL_WARNING_TITLE = 'Email preferred for this contact'
